<template>
  <b-card class="fundletters-card" no-body>
    <h1>{{ title }}</h1>

    <div
      class="year-actions-row d-flex justify-content-between align-items-center flex-wrap my-3"
    >
      <div class="year-tabs d-flex flex-wrap gap-2">
        <b-button
          v-for="year in availableYears"
          :key="year"
          :variant="selectedYear === year ? 'primary' : 'outline-primary'"
          @click="selectedYear = year"
        >
          {{ year }}
        </b-button>
      </div>

      <div class="buttons-wrapper d-flex flex-column gap-2">
            <!-- <b-button variant="primary" @click="addDocument">
          <i class="fas fa-plus me-1"></i>
          Adicionar
        </b-button> -->
        <b-button
          variant="outline-primary"
          @click="updateDocuments"
          :disabled="isUpdating"
        >
          <i class="fas fa-sync me-1"></i>
          Atualizar
        </b-button>

        <b-progress
          v-if="isUpdating"
          :value="progress"
          max="100"
          height="15px"
          animated
          variant="primary"
        ></b-progress>
      </div>
    </div>

    <b-card-body>
      <div class="fundletters-category-buttons">
        <button
          v-for="(category, index) in categories"
          :key="index"
          :class="[
            'fundletters-toggle-btn',
            { active: activeCategory === index },
          ]"
          @click="toggleCategory(index)"
        >
          {{ category.name }}
        </button>
      </div>

      <div
        v-if="activeCategory !== null"
        class="fundletters-category-content-scroll"
      >
        <div
          v-for="(letter, idx) in filteredCategories[activeCategory].letters"
          :key="idx"
          class="fundletters-letter-card-wrapper"
        >
          <div class="fundletters-letter-card" @click="toggleLetter(idx)">
            <strong>{{ letter.name }}</strong>
          </div>

          <transition name="fade">
            <ul v-show="letter.expanded" class="fundletters-letter-items">
              <li
                v-for="(item, i) in letter.items"
                :key="i"
                class="fundletters-letter-item d-flex justify-content-between align-items-center"
              >
                <div class="fundletters-item-left">
                  <span class="fundletters-item-title">{{ item.title }}</span>
                  <span class="fundletters-item-date text-muted">{{
                    item.date
                  }}</span>
                </div>

                <div class="d-flex align-items-center gap-2">
                  <b-button
                    variant="link"
                    size="sm"
                    class="p-0"
                    title="Download"
                    @click.stop="downloadFile(item.url, item.title)"
                  >
                    <i class="fas fa-download"></i>
                  </b-button>

                  <b-button
                    variant="link"
                    size="sm"
                    class="p-0"
                    title="Visualizar"
                    @click.stop="openViewer(item.url, item.title)"
                  >
                    <i class="fas fa-eye"></i>
                  </b-button>
                </div>
              </li>
            </ul>
          </transition>
        </div>
      </div>
    </b-card-body>

    <b-modal
      id="letters-viewer-modal"
      size="xl"
      :title="`Visualizar Carta - ${currentDocument || ''}`"
      hide-footer
    >
      <div v-if="!iframeBlocked">
        <iframe
          v-if="currentUrl"
          :src="currentUrl"
          width="100%"
          height="800px"
          style="border: none"
        ></iframe>
      </div>

      <div v-else class="text-center py-5">
        <p class="mb-3">
          Este site não permite ser visualizado dentro da plataforma. <br />
          Para acessá-lo, clique no botão abaixo:
        </p>
        <b-button
          :href="currentUrl"
          target="_blank"
          variant="primary"
          rel="noopener noreferrer"
        >
          Abrir em nova aba
        </b-button>
      </div>
    </b-modal>

    <AddDocumentModal
      ref="addDocumentModal"
      @save-document="handleSaveDocument"
    />
  </b-card>
</template>

<script>
import { BCard, BCardBody, BButton, BModal, BProgress } from "bootstrap-vue";
import api from "@/views/pages/FundsHub/Requests.js";
import AddDocumentModal from "./AddDocumentModal.vue";

export default {
  name: "FundLetters",
  components: {
    BCard,
    BCardBody,
    BButton,
    BModal,
    BProgress,
    AddDocumentModal,
  },
  data() {
    return {
      title: "Cartas",
      activeCategory: null,
      currentUrl: null,
      currentDocument: null,
      categories: [],
      selectedYear: null,
      availableYears: [],
      iframeBlocked: false,
      isUpdating: false,
      progress: 0,
    };
  },
  computed: {
    filteredCategories() {
      if (!this.selectedYear) return this.categories;
      return this.categories.map((category) => ({
        ...category,
        letters: category.letters.map((letter) => ({
          ...letter,
          items: letter.items.filter((item) =>
            item.date.includes(this.selectedYear)
          ),
        })),
      }));
    },
  },
  created() {
    this.fetchCategories();
  },
  methods: {
    async fetchCategories() {
      try {
        const response = await api.getGroupedLetters();
        const categories = response.data.map((category) => ({
          name: category.name,
          letters: category.letters.map((letterGroup) => ({
            ...letterGroup,
            expanded: false,
          })),
        }));

        const allYears = new Set();
        categories.forEach((category) => {
          category.letters.forEach((letter) => {
            letter.items.forEach((item) => {
              const year = item.date.split("/")[2];
              if (year) allYears.add(year);
            });
          });
        });

        this.availableYears = Array.from(allYears).sort((a, b) => b - a);
        this.selectedYear = this.availableYears[0] || null;
        this.categories = categories;
      } catch (error) {
        console.error("Erro ao carregar categorias de cartas:", error);
      }
    },
    toggleCategory(index) {
      this.activeCategory = this.activeCategory === index ? null : index;
    },
    toggleLetter(idx) {
      const currentLetters = this.categories[this.activeCategory].letters;
      currentLetters[idx].expanded = !currentLetters[idx].expanded;
    },
    openViewer(url, title) {
      this.currentDocument = title;
      if (url.toLowerCase().endsWith(".pdf")) {
        this.currentUrl = url;
        this.iframeBlocked = false;
        this.$root.$emit("bv::show::modal", "letters-viewer-modal");
      } else {
        window.open(url, "_blank", "noopener");
      }
    },

    downloadFile(url) {
      window.open(url, "_blank", "noopener");
    },

    async updateDocuments() {
      this.isUpdating = true;
      this.progress = 0;


      const interval = setInterval(() => {
        if (this.progress < 80) {
          this.progress += 5; 
        }
      }, 200);

      try {
        await api.updateLetters();
        // aguarda um pouquinho pra finalizar a animação
        await new Promise((resolve) => setTimeout(resolve, 500));

        this.progress = 100;
        this.$bvToast.toast("Cartas atualizadas com sucesso", {
          title: "Sucesso",
          variant: "success",
          solid: true,
          autoHideDelay: 3000,
        });
        await this.fetchCategories();
      } catch (error) {
        console.error("Erro ao atualizar documentos:", error);
        this.$bvToast.toast("Erro ao atualizar os documentos.", {
          title: "Erro",
          variant: "danger",
          solid: true,
          autoHideDelay: 4000,
        });
      } finally {
        clearInterval(interval);
        setTimeout(() => {
          this.isUpdating = false;
          this.progress = 0;
        }, 500);
      }
    },

    // addDocument() {
    //   if (this.$refs.addDocumentModal.show) {
    //     this.$refs.addDocumentModal.show();
    //   }
    // },

    handleSaveDocument(newDoc) {
      api
        .addLetter({
          name: newDoc.manager,
          website_url: newDoc.website_url,
          category: newDoc.fund_type,
        })
        .then(() => {
          this.$bvToast.toast("Instituição adicionada com sucesso.", {
            title: "Sucesso",
            variant: "success",
            solid: true,
            autoHideDelay: 3000,
          });
          this.fetchCategories();
        })
        .catch(() => {
          this.$bvToast.toast("Erro ao adicionar instituição.", {
            title: "Erro",
            variant: "danger",
            solid: true,
            autoHideDelay: 4000,
          });
        });
    },
  },
};
</script>

<style lang="scss">
@import "@/@core/scss/base/components/fund-letters.scss";
</style>
